import React from 'react';
import { Info, TrendingUp, Calculator, PieChart, AlertTriangle, FileText, BookOpen, Book } from 'lucide-react';

/**
 * Компонент с информацией о коэффициентах ликвидности и их анализе
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Коэффициенты ликвидности: теория, практика, нормативы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-indigo-500" />
          Что такое ликвидность предприятия?
        </h3>
        <p>
          <strong>Ликвидность</strong> — это способность предприятия погашать свои краткосрочные обязательства за счет оборотных активов. Другими словами, это финансовая гибкость компании, позволяющая ей своевременно отвечать по долгам и избегать кассовых разрывов. Ликвидность является одним из ключевых показателей финансовой устойчивости бизнеса и активно используется кредиторами, инвесторами и регуляторами для оценки надежности компании.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Основные коэффициенты ликвидности
        </h3>
        <p>
          Для анализа ликвидности используются несколько ключевых коэффициентов, каждый из которых оценивает способность компании погашать обязательства, но с разной степенью строгости к составу ликвидных активов:
        </p>
        <ul>
          <li>
            <strong>Коэффициент текущей ликвидности</strong> (Current Ratio) — отношение всех оборотных активов к краткосрочным обязательствам. Показывает, насколько компания может погасить свои краткосрочные обязательства за счет всех оборотных активов.
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg my-2">
              <code className="text-sm">Ктл = Оборотные активы / Краткосрочные обязательства</code>
            </div>
          </li>
          <li>
            <strong>Коэффициент быстрой ликвидности</strong> (Quick Ratio, Acid-Test Ratio) — отношение наиболее ликвидных активов (денежные средства, краткосрочные финансовые вложения и дебиторская задолженность) к краткосрочным обязательствам. Исключает из расчета запасы, как менее ликвидные активы.
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg my-2">
              <code className="text-sm">Кбл = (Денежные средства + Краткосрочные фин. вложения + Дебиторская задолженность) / Краткосрочные обязательства</code>
            </div>
          </li>
          <li>
            <strong>Коэффициент абсолютной ликвидности</strong> (Cash Ratio) — отношение наиболее ликвидных активов (денежные средства и краткосрочные финансовые вложения) к краткосрочным обязательствам. Показывает, какая часть краткосрочных обязательств может быть погашена немедленно.
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg my-2">
              <code className="text-sm">Кал = (Денежные средства + Краткосрочные фин. вложения) / Краткосрочные обязательства</code>
            </div>
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <PieChart className="w-5 h-5 text-indigo-500" />
          Нормативные значения коэффициентов ликвидности
        </h3>
        <p>
          В России нормативные значения коэффициентов ликвидности могут отличаться в зависимости от отрасли, размера компании и экономических условий. Однако существуют общепринятые диапазоны, которые считаются оптимальными:
        </p>
        
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Коэффициент
                </th>
                <th scope="col" className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Нормативное значение
                </th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Комментарий
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Коэффициент текущей ликвидности
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-300 font-medium">
                  1.5 - 2.5
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
                  В России официальный норматив для большинства отраслей составляет ≥ 2.0
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Коэффициент быстрой ликвидности
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-300 font-medium">
                  0.7 - 1.5
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
                  Значение ≥ 1.0 считается хорошим показателем для большинства отраслей
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Коэффициент абсолютной ликвидности
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-300 font-medium">
                  0.2 - 0.5
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
                  Минимальное значение в России обычно принимается равным 0.2
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <p>
          Важно отметить, что эти нормативы могут существенно отличаться в зависимости от специфики отрасли. Например, для торговых предприятий с высокой оборачиваемостью допустимы более низкие значения коэффициентов ликвидности, чем для производственных компаний с длительным циклом.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Отраслевая специфика в России
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            В России отраслевые нормативы могут устанавливаться профильными министерствами и ведомствами. Например, Минфин России рекомендует для анализа финансового состояния предприятий использовать следующие критерии: для промышленности коэффициент текущей ликвидности должен быть не менее 1.7, для торговли — не менее 1.3, для сельского хозяйства — не менее 1.5.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-indigo-500" />
          Интерпретация результатов
        </h3>
        <p>
          При анализе коэффициентов ликвидности важно учитывать не только соответствие нормативам, но и динамику показателей, а также их соотношение друг с другом:
        </p>
        <ul>
          <li>
            <strong>Слишком высокие значения</strong> (например, коэффициент текущей ликвидности &gt; 3) могут свидетельствовать о неэффективном использовании оборотных активов, избыточном накоплении запасов или нерациональной структуре капитала.
          </li>
          <li>
            <strong>Слишком низкие значения</strong> указывают на потенциальные проблемы с платежеспособностью и повышенный риск банкротства.
          </li>
          <li>
            <strong>Значительное расхождение</strong> между коэффициентами текущей и быстрой ликвидности (более чем в 2 раза) может свидетельствовать о избыточных запасах или проблемах с их оборачиваемостью.
          </li>
          <li>
            <strong>Устойчивое снижение</strong> коэффициентов в динамике — тревожный сигнал, даже если текущие значения соответствуют нормативам.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-500" />
          Анализ ликвидности баланса
        </h3>
        <p>
          Помимо расчета коэффициентов, важно проводить анализ ликвидности баланса, который позволяет оценить, насколько структура активов соответствует срочности обязательств. Для этого активы и пассивы группируются:
        </p>
        <ul>
          <li><strong>A1</strong> — наиболее ликвидные активы (денежные средства и краткосрочные фин. вложения)</li>
          <li><strong>A2</strong> — быстрореализуемые активы (дебиторская задолженность)</li>
          <li><strong>A3</strong> — медленно реализуемые активы (запасы, НДС, прочие оборотные активы)</li>
          <li><strong>A4</strong> — труднореализуемые активы (внеоборотные активы)</li>
          <li><strong>П1</strong> — наиболее срочные обязательства (кредиторская задолженность)</li>
          <li><strong>П2</strong> — краткосрочные пассивы (краткосрочные кредиты и займы)</li>
          <li><strong>П3</strong> — долгосрочные пассивы (долгосрочные обязательства)</li>
          <li><strong>П4</strong> — постоянные пассивы (собственный капитал)</li>
        </ul>
        <p>
          Баланс считается абсолютно ликвидным, если выполняются все неравенства: A1 ≥ П1, A2 ≥ П2, A3 ≥ П3, A4 ≤ П4.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Особенности учета в РСБУ и МСФО
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>В РСБУ оценка многих активов может не соответствовать их реальной рыночной стоимости</li>
            <li>МСФО требует более детального раскрытия информации о ликвидности активов</li>
            <li>В МСФО используется концепция "справедливой стоимости", что может влиять на значения коэффициентов</li>
            <li>При анализе компаний, ведущих отчетность по разным стандартам, необходимо учитывать эти различия</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Book className="w-5 h-5 text-indigo-500" />
          Законодательное регулирование в России
        </h3>
        <p>
          В России коэффициенты ликвидности используются в нескольких официальных методиках:
        </p>
        <ul>
          <li>
            <strong>Методика Федеральной налоговой службы</strong> для определения платежеспособности должника и выявления признаков фиктивного или преднамеренного банкротства.
          </li>
          <li>
            <strong>Постановление Правительства РФ от 25.06.2003 № 367</strong>, которое устанавливает правила анализа финансового состояния должника арбитражными управляющими.
          </li>
          <li>
            <strong>Методические указания по анализу финансово-хозяйственной деятельности</strong> различных министерств и ведомств.
          </li>
        </ul>
        <p>
          Для предприятий некоторых отраслей (например, банки, страховые компании) показатели ликвидности являются не только аналитическими инструментами, но и обязательными нормативами, невыполнение которых может привести к санкциям со стороны регулирующих органов.
        </p>

        <h3>Способы улучшения показателей ликвидности</h3>
        <p>
          Если анализ выявил недостаточную ликвидность предприятия, можно рассмотреть следующие меры:
        </p>
        <ol>
          <li><strong>Сокращение запасов</strong> до оптимального уровня через улучшение логистики и планирования закупок</li>
          <li><strong>Ускорение оборачиваемости дебиторской задолженности</strong> через совершенствование системы управления, включая скидки за раннюю оплату, факторинг и т.д.</li>
          <li><strong>Реструктуризация задолженности</strong> — перевод краткосрочных обязательств в долгосрочные</li>
          <li><strong>Привлечение долгосрочного финансирования</strong> для увеличения собственного капитала</li>
          <li><strong>Оптимизация управления денежными потоками</strong> для обеспечения необходимого уровня денежных средств</li>
          <li><strong>Продажа неиспользуемых или неэффективных активов</strong> для увеличения ликвидных средств</li>
        </ol>

        <p className="font-semibold mt-6">
          Регулярный анализ коэффициентов ликвидности позволяет своевременно выявить потенциальные финансовые проблемы и принять меры для их устранения, что является неотъемлемой частью эффективного финансового менеджмента в современных российских условиях.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;